import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthPage } from '../AuthPage';
import type { PaymentPlan } from '../PaymentPanel';
import { defaultAuthenticationSettings } from '../../data/mockData';
import { CRM_PLAN_FALLBACK } from '../../config/crmPlans';
import { getCrmPlans, initializeCrmSignup, type CrmPlanOption } from '../../api/crm';
import { updateUser } from '../../api/users';
import safeToast from '../../utils/safeToast';
import { ensureOwnerSessionFromProfile, waitForOwnerSession } from '../../utils/ownerSession';
import { dispatchOwnerTokenEvent } from '../../utils/ownerTokenEvents';
import { useLocalizedCrmPlans } from '../../utils/localizedCrmPlans';

const PaymentPanel = React.lazy(() => import('../PaymentPanel'));

/** CRM signup skips card-template picking — default template is assigned automatically. */
type FlowStep = 'plan' | 'signup' | 'payment';
const STEPS: FlowStep[] = ['plan', 'signup', 'payment'];
const DEFAULT_CRM_TEMPLATE_ID = '1';

interface CrmSignupModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (opts: { paymentSucceeded: boolean }) => void;
}

async function waitForOwnerSessionWithProfile(profile: Record<string, any> | null, timeoutMs = 10000): Promise<boolean> {
  return waitForOwnerSession(
    {
      email: profile?.email,
      password: profile?.password,
      username: profile?.username,
      id: profile?.id,
      _id: profile?._id,
    },
    timeoutMs,
  );
}

export default function CrmSignupModal({ open, onClose, onComplete }: CrmSignupModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<FlowStep>('plan');
  const [plans, setPlans] = useState<CrmPlanOption[]>(CRM_PLAN_FALLBACK);
  const localizedPlans = useLocalizedCrmPlans(plans);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<CrmPlanOption | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('plan');
    setSelectedPlan(null);
    setUserData(null);
    setAdvancing(false);
    setLoadingPlans(true);
    (async () => {
      try {
        const remote = await getCrmPlans();
        if (remote.length) setPlans(remote);
      } catch {}
    })().finally(() => setLoadingPlans(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const paymentPlan: PaymentPlan | null = useMemo(() => {
    if (!selectedPlan) return null;
    const localized = localizedPlans.find((p) => p.planId === selectedPlan.planId) || selectedPlan;
    return {
      planId: localized.planId,
      name: localized.name,
      price: localized.price,
      description: localized.description,
      features: localized.features,
    };
  }, [selectedPlan, localizedPlans]);

  const stepLabels: Record<FlowStep, string> = useMemo(() => ({
    plan: t('crmSignupStepPlan'),
    signup: t('crmSignupStepSignup'),
    payment: t('crmSignupStepPayment'),
  }), [t]);

  const finishSignup = useCallback(async (paymentSucceeded: boolean) => {
    let profile: any = userData;
    try {
      const raw = localStorage.getItem('template_selection_user');
      profile = raw ? JSON.parse(raw) : userData;
    } catch {}

    let ready = await waitForOwnerSessionWithProfile(profile);
    if (!ready) {
      ready = await waitForOwnerSessionWithProfile(profile, 15000);
    }
    if (!ready) {
      safeToast('error', t('crmSignupSessionTimeout'));
      return;
    }

    try {
      const token = localStorage.getItem('owner_token') || '';
      if (token) {
        localStorage.setItem('crm_token', token);
        dispatchOwnerTokenEvent();
      }
    } catch {}

    onClose();
    onComplete({ paymentSucceeded });
  }, [onClose, onComplete, userData, t]);

  const handlePlanChoose = (plan: CrmPlanOption) => {
    setSelectedPlan(plan);
    setStep('signup');
  };

  /** Assign default card template + init CRM org, then open payment. */
  const advanceToPayment = useCallback(async (signupData: any, plan: CrmPlanOption) => {
    setAdvancing(true);
    try {
      try {
        const token = localStorage.getItem('owner_token');
        const patch = { templateId: DEFAULT_CRM_TEMPLATE_ID, templateMetaId: null };
        if (token) {
          const idOrUsername = signupData.id || signupData._id || signupData.username;
          if (idOrUsername) await updateUser(String(idOrUsername), patch as any).catch(() => null);
        }
      } catch {}

      const sessionReady = await ensureOwnerSessionFromProfile({
        email: signupData.email,
        password: signupData.password,
        username: signupData.username,
        id: signupData.id,
        _id: signupData._id,
      });
      if (!sessionReady) {
        safeToast('error', t('crmSignupSetupFailed'));
        return;
      }

      try {
        const token = localStorage.getItem('owner_token') || '';
        if (token) localStorage.setItem('crm_token', token);
        await initializeCrmSignup({
          planId: plan.planId,
          companyName: signupData.company || signupData.name,
          crmCategory: signupData.crmCategory || undefined,
        });
      } catch (e: any) {
        safeToast('warning', e?.message || t('crmSignupCrmSetupWarning'));
      }

      setStep('payment');
    } finally {
      setAdvancing(false);
    }
  }, [t]);

  const handleUserSignup = (signupData: any) => {
    try { localStorage.setItem('template_selection_user', JSON.stringify(signupData)); } catch {}
    setUserData(signupData);
    if (!selectedPlan) {
      safeToast('error', t('crmSignupSetupFailed'));
      return;
    }
    void advanceToPayment(signupData, selectedPlan);
  };

  if (!open) return null;

  const stepIndex = STEPS.indexOf(step);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[980px] max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">{t('crmSignupTitle')}</h2>
              <p className="text-sm text-slate-500 mt-1">{t('crmSignupDesc')}</p>
            </div>
            <button type="button" onClick={onClose} className="w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-white">×</button>
          </div>
          <div className="flex gap-2 mt-4">
            {STEPS.map((s, idx) => (
              <div key={s} className="flex-1">
                <div className={`h-1 rounded-full ${idx <= stepIndex ? 'bg-blue-600' : 'bg-slate-200'}`} />
                <p className={`text-[11px] mt-1 text-center ${s === step ? 'font-bold text-blue-700' : 'text-slate-400'}`}>{stepLabels[s]}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 sm:p-6">
          {step === 'plan' && (
            loadingPlans ? (
              <div className="text-center py-16 text-slate-400">{t('crmSignupLoadingPlans')}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {localizedPlans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => handlePlanChoose(plan)}
                    className="text-left rounded-2xl border border-slate-200 p-5 hover:border-blue-500 hover:shadow-lg transition-all bg-white"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-slate-900">{plan.name}</h3>
                        <p className="text-2xl font-extrabold text-blue-700 mt-1">
                          {plan.price}
                          <span className="text-sm font-medium text-slate-500">
                            {plan.planId === 'crm_enterprise' ? t('crmSignupPricePerBranch') : t('crmSignupPricePerMonth')}
                          </span>
                        </p>
                      </div>
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                        {plan.planId === 'crm_enterprise'
                          ? t('crmSignupPerBranch')
                          : plan.seatLimit == null
                            ? t('crmSignupUnlimitedTeam')
                            : t('crmSignupTeamSeats', { count: plan.seatLimit })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-3">{plan.description}</p>
                    <ul className="mt-3 space-y-1">
                      {(plan.features || []).slice(0, 4).map((f) => (
                        <li key={f} className="text-xs text-slate-600 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
            )
          )}

          {step === 'signup' && (
            advancing ? (
              <div className="text-center py-16 text-slate-400">{t('crmSignupPreparingPayment')}</div>
            ) : (
              <AuthPage
                authType="user"
                initialView="signup"
                embedded
                compact
                signupOnly
                hideExit
                crmFlow
                backLabel={t('crmSignupBackPackages')}
                onBack={() => setStep('plan')}
                onAdminLogin={() => {}}
                onUserSignup={handleUserSignup}
                authSettings={defaultAuthenticationSettings}
              />
            )
          )}

          {step === 'payment' && userData && paymentPlan && (
            <Suspense fallback={<div className="py-16 text-center text-slate-400">{t('crmSignupLoadingPayment')}</div>}>
              <PaymentPanel
                embedded
                user={{
                  id: userData?.id || userData?._id,
                  name: userData?.name,
                  email: userData?.email,
                  phone: userData?.phone,
                  company: userData?.company,
                  position: userData?.position,
                  location: userData?.location,
                }}
                plan={paymentPlan}
                onSuccess={() => {
                  safeToast('success', t('crmSignupPaymentSuccess'));
                  void finishSignup(true);
                }}
                onFailure={() => {
                  safeToast('info', t('crmSignupPaymentIncomplete'));
                  void finishSignup(false);
                }}
                onCancel={() => setStep('signup')}
              />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
}
